import { AssetInfoCard } from "components/AssetInfoCard";
import { ManageAssetCard } from "components/liquidity-staking/ManageAssetCard";
import { ManagePoolCard } from "components/liquidity-staking/ManagePoolCard";
import { ValidatorInfoCard } from "components/liquidity-staking/ValidatorInfoCard";
import { PoolResponse } from "models/staking-liquidity/PoolResponse";
import { StakingInfoResponse } from "models/staking-liquidity/StakingInfoResponse";
import { useWallet } from "provider/WalletProvider";
import { useEffect, useState } from "react";
import { LIQUIDITY_STAKING_ADDRESS } from "utils/Constants";
import { queryDelegation, queryLuncBalance, querySmartContract } from "utils/RpcHelpers";
import { Coin } from '@goblinhunt/cosmes/registry'
import { currency, fromDenom, toDenom } from "utils/Helpers";
import { RewardResponse } from "models/staking-liquidity/RewardResponse";
import { showSimpleModal } from "utils/Modals";
import { StakeResponse } from "models/staking-liquidity/StakeResponse";
import { ManageUnstakeCard } from "components/liquidity-staking/ManageUnstakeCard";
import { UnstakeResponse } from "models/staking-liquidity/UnstakeResponse";
import { ValidatorDetailCard } from "components/liquidity-staking/ValidatorDetailCard";

export function LiquidityStakingPage() {
    const [totalStaker, setTotalStaker] = useState(0);
    const [totalStakedAsset, setTotalStakedAsset] = useState(0);
    const [stakingInfo, setStakingInfo] = useState<StakingInfoResponse>();
    const [poolInfo, setPoolInfo] = useState<PoolResponse>();
    const [balance, setBalance] = useState<Coin>()
    const [stake, setStake] = useState<StakeResponse>()
    const [unstake, setUnstake] = useState<UnstakeResponse>()
    const [reward, setReward] = useState<RewardResponse>({ token_reward: "0", uluna_reward: "0", uusd_reward: "0" })
    const { address } = useWallet()

    const loadUnstake = async () => {
        const unstakeInfo = await querySmartContract(LIQUIDITY_STAKING_ADDRESS, { unstake: { staker: address! } })
        setUnstake(unstakeInfo)
    }
    const luncTokenInfo = {
        decimals: 6,
        name: "LUNC",
        symbol: "LUNC",
        total_supply: "0"
    }

    const loadBalance = async () => {
        const balance = await queryLuncBalance(address!)
        console.log(balance)
        setBalance(balance)
    }

    const loadPoolInfo = async () => {
        const poolInfo = await querySmartContract(LIQUIDITY_STAKING_ADDRESS, { pool: {} })
        setPoolInfo(poolInfo)
    }

    const loadStake = async () => {
        const stake = await querySmartContract(LIQUIDITY_STAKING_ADDRESS, { stake: { staker: address! } })
        setStake(stake)
    }

    const loadReward = async () => {
        try {
            const res: RewardResponse = await querySmartContract(LIQUIDITY_STAKING_ADDRESS!, {
                reward: {
                    staker: address
                }
            })
            setReward(res)
        } catch (e) { }
    }

    useEffect(() => {
        loadPoolInfo()
    }, [])

    useEffect(() => {
        if (address) {
            loadBalance()
            loadStake()
            loadUnstake()
        }
    }, [address])

    useEffect(() => {
        const loadRewardEverySeconds = () => {
            if (address) loadReward()
        };

        const timerInterval = setInterval(loadRewardEverySeconds, 1000);
        loadRewardEverySeconds();

        return () => clearInterval(timerInterval);
    }, [address]);

    const loadAddresses = async (validatorAddr: string) => {
        let startAfter = undefined;
        let totalStaker = 0;
        let totalStaked = 0;

        while (true) {
            const lastStartAfter = startAfter;
            const { addresses } = await querySmartContract(LIQUIDITY_STAKING_ADDRESS, { all_staker: { limit: 10 } })
            for (const address of addresses) {
                const res = await queryDelegation(validatorAddr, address)
                const amount = Number(res?.balance?.amount)
                if (amount > 0) {
                    totalStaker++;
                    totalStaked += amount;
                }
                startAfter = address;
            }
            if (startAfter == lastStartAfter) break;
        }
        setTotalStakedAsset(totalStaked)
        setTotalStaker(totalStaker)
    }

    useEffect(() => {
        if (stakingInfo) loadAddresses(stakingInfo.config.validator_address)
    }, [stakingInfo])

    return <div className="container-fluid container-p-y">
        <div className="row">
            <div className="col-12 col-md-7 mb-3">
                <div className="row">
                    <div className="col-12 mb-3">
                        {
                            stakingInfo && <ValidatorDetailCard validatorAddress={stakingInfo.config.validator_address} />
                        }
                    </div>
                    <div className="col-12">
                        <ValidatorInfoCard
                            onStakingInfoLoaded={e => setStakingInfo(e)}
                            poolInfo={poolInfo}
                        />
                    </div>
                </div>
            </div>
            <div className="col-12 col-md-5">
                <div className="row">
                    <div className="col-6 mb-3">
                        <AssetInfoCard
                            amount={fromDenom(Number(stake?.amount ?? "0"), 6)}
                            tokenInfo={luncTokenInfo}
                            icon={"dollar"}
                            color={"success"}
                            label={"Staked Asset"} />
                    </div>
                    <div className="col-6 mb-3">
                        <AssetInfoCard
                            amount={fromDenom(Number(reward.uluna_reward), 6)}
                            tokenInfo={luncTokenInfo}
                            icon={"dollar"}
                            color={"warning"}
                            label={"Your Reward"}
                            onShowDetail={() => showSimpleModal(
                                "Your Reward",
                                <div>
                                    <div className="d-flex align-items-center gap-1">
                                        <h6 className="mb-0">{currency(fromDenom(Number(reward.uluna_reward), 6))}</h6>
                                        <span className="text-muted">LUNC</span>
                                    </div>
                                    <div className="d-flex align-items-center gap-1">
                                        <h6 className="mb-0">{currency(fromDenom(Number(reward.uusd_reward), 6))}</h6>
                                        <span className="text-muted">USTC</span>
                                    </div>
                                    <div className="d-flex align-items-center gap-1">
                                        <h6 className="mb-0">{currency(fromDenom(Number(reward.token_reward), stakingInfo?.token_info.decimals ?? 6))}</h6>
                                        <span className="text-muted">{stakingInfo?.token_info.symbol}</span>
                                    </div>
                                </div>
                            )}
                        />
                    </div>
                    <div className="col-6 mb-3">
                        <AssetInfoCard
                            amount={fromDenom(Number(poolInfo?.pool.total_pool ?? "0"), stakingInfo?.token_info.decimals ?? 6)}
                            tokenInfo={stakingInfo?.token_info}
                            icon={"dollar"}
                            color={"info"}
                            label={"Reward Pool"} />
                    </div>
                    <div className="col-6 mb-3">
                        <AssetInfoCard
                            amount={fromDenom(Number(balance?.amount ?? "0"), 6)}
                            tokenInfo={luncTokenInfo}
                            icon={"dollar"}
                            color={"primary"}
                            label={"Your Balance"} />
                    </div>
                    <div className="col-6 mb-3">
                        <AssetInfoCard
                            amount={totalStaker}
                            icon={"group"}
                            color={"secondary"}
                            label={"Total Staker"} />
                    </div>
                    <div className="col-6 mb-3">
                        <AssetInfoCard
                            amount={fromDenom(totalStakedAsset, 6)}
                            tokenInfo={luncTokenInfo}
                            icon={"dollar-circle"}
                            color={"danger"}
                            label={"Total Staked Asset"} />
                    </div>
                    {
                        address && <div className="col-12 mb-3">
                            <ManageAssetCard
                                stakeInfo={stake}
                                balance={balance}
                                stakingInfo={stakingInfo}
                                onRefresh={() => {
                                    loadBalance()
                                }}
                            />
                        </div>
                    }

                    {
                        (address && stakingInfo?.config.owner == address) && <div className="col-12 mb-3">
                            <ManagePoolCard
                                stakingInfo={stakingInfo!}
                                pool={poolInfo}
                                onRefresh={() => loadPoolInfo()}
                            />
                        </div>
                    }

                    {
                        (address && ((unstake?.unstake.length ?? 0) > 0)) && <div className="col-12">
                            <ManageUnstakeCard
                                tokenInfo={luncTokenInfo}
                                contractAddress={LIQUIDITY_STAKING_ADDRESS}
                                unstake={unstake!} />
                        </div>
                    }

                </div>
            </div>
        </div>
    </div>
}